import { memo, useEffect, useState } from 'react';
import {
  X,
  Coins,
  ArrowDownCircle,
  ArrowUpCircle,
  Clock,
  Zap,
  Shield,
  FlaskConical,
  User,
  CreditCard,
  Infinity as InfinityIcon,
} from 'lucide-react';
import { useCreditsStore } from '../../store/creditsStore';
import BuyCreditsModal from './BuyCreditsModal';

const RESULT_TYPE_LABELS = {
  enhance_photo: 'Mejorar Imagen',
  create_video: 'Crear Video',
  nano_banana: 'Nano Banana',
  background_removal: 'Remover Fondo',
  video_with_sound: 'Video + Audio',
  fabric_avatar: 'Avatar Parlante',
  dream_actor: 'DreamActor',
  lyria2: 'Lyria 2',
  qwen3_tts: 'Qwen3 TTS',
  style_transfer: 'Transferir Estilo',
  upscale: 'Upscale',
  frame_extractor: 'Frame Extractor',
  lyria3_pro: 'Lyria 3 Full Song',
  lmx: 'LMX · asistente',
};

const ROLE_DISPLAY = {
  admin: { label: 'Administrador', color: '#a855f7', Icon: Shield },
  tester: { label: 'Tester', color: '#06b6d4', Icon: FlaskConical },
  user: { label: 'Usuario', color: '#10b981', Icon: User },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CreditsPanel = memo(({ isOpen, onClose }) => {
  const balance = useCreditsStore(s => s.balance);
  const totalSpent = useCreditsStore(s => s.totalSpent);
  const role = useCreditsStore(s => s.role);
  const userProfile = useCreditsStore(s => s.userProfile);
  const costs = useCreditsStore(s => s.costs);
  const history = useCreditsStore(s => s.history);
  const fetchBalance = useCreditsStore(s => s.fetchBalance);
  const fetchCosts = useCreditsStore(s => s.fetchCosts);
  const fetchHistory = useCreditsStore(s => s.fetchHistory);

  const [activeTab, setActiveTab] = useState('profile');
  const [showBuyModal, setShowBuyModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchBalance();
      fetchCosts();
      fetchHistory();
    }
  }, [isOpen, fetchBalance, fetchCosts, fetchHistory]);

  if (!isOpen) return null;

  const roleConfig = ROLE_DISPLAY[role] || ROLE_DISPLAY.user;
  const RoleIcon = roleConfig.Icon;

  return (
    <div className="credits-panel-overlay" onClick={onClose} data-testid="credits-panel-overlay">
      <div
        className="credits-panel credits-panel-open"
        onClick={(e) => e.stopPropagation()}
        data-testid="credits-panel"
      >
        {/* Header */}
        <div className="credits-panel-header">
          <div className="credits-panel-title">
            <Coins size={18} />
            <span>Créditos</span>
            {role !== 'user' && (
              <span className={`credits-role-tag ${role}`} data-testid="credits-role-tag">
                <RoleIcon size={12} />
                {roleConfig.label}
              </span>
            )}
          </div>
          <button className="credits-panel-close" onClick={onClose} data-testid="credits-panel-close">
            <X size={18} />
          </button>
        </div>

        {/* Balance Card — unified for all roles */}
        <div className="credits-balance-card" data-testid="credits-balance-card">
          <div className="credits-balance-label">Saldo Disponible</div>
          <div className={`credits-balance-amount ${balance !== null && balance <= 0 ? 'empty' : balance !== null && balance < 5 ? 'low' : ''}`}>
            <Coins size={28} />
            <span data-testid="credits-panel-balance">{balance !== null ? balance : '--'}</span>
          </div>
          {balance !== null && balance <= 0 && (
            <div className="credits-balance-warning" data-testid="credits-insufficient-warning">
              Sin créditos disponibles
              <button
                className="credits-warning-buy-link"
                onClick={() => setShowBuyModal(true)}
                data-testid="credits-warning-buy-btn"
              >
                Cargar créditos
              </button>
            </div>
          )}
          {balance !== null && balance > 0 && balance < 5 && (
            <div className="credits-balance-warning low" data-testid="credits-low-warning">
              Créditos bajos — solo pagas lo que usas
              <button
                className="credits-warning-buy-link"
                onClick={() => setShowBuyModal(true)}
              >
                Cargar créditos
              </button>
            </div>
          )}
        </div>

        {/* Buy Credits Button — always visible */}
        <button
          className="buy-credits-trigger-btn"
          onClick={() => setShowBuyModal(true)}
          data-testid="buy-credits-btn"
        >
          <CreditCard size={14} />
          Comprar Créditos
        </button>

        {/* Tabs */}
        <div className="credits-tabs">
          <button
            className={`credits-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
            data-testid="credits-tab-profile"
          >
            <User size={14} />
            Perfil
          </button>
          <button
            className={`credits-tab ${activeTab === 'costs' ? 'active' : ''}`}
            onClick={() => setActiveTab('costs')}
            data-testid="credits-tab-costs"
          >
            <Zap size={14} />
            Costos
          </button>
          <button
            className={`credits-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
            data-testid="credits-tab-history"
          >
            <Clock size={14} />
            Historial
          </button>
        </div>

        {/* Tab Content */}
        <div className="credits-tab-content">
          {/* PROFILE TAB */}
          {activeTab === 'profile' && (
            <div className="credits-profile-section" data-testid="credits-profile-section">
              {/* Role card */}
              <div className="credits-profile-role-card" style={{ borderColor: `${roleConfig.color}40` }}>
                <div className="credits-profile-role-icon" style={{ background: `${roleConfig.color}18`, color: roleConfig.color }}>
                  <RoleIcon size={24} />
                </div>
                <div className="credits-profile-role-info">
                  <span className="credits-profile-role-label" data-testid="credits-user-role">
                    {roleConfig.label}
                  </span>
                  <span className="credits-profile-role-desc">
                    {role === 'admin' && 'Acceso administrativo — créditos trazables como todo usuario'}
                    {role === 'tester' && 'Usuario de pruebas — créditos trazables'}
                    {role === 'user' && 'Créditos deducidos por operación'}
                  </span>
                </div>
              </div>

              {/* User details */}
              {userProfile && (
                <div className="credits-profile-details">
                  <div className="credits-profile-row">
                    <span className="credits-profile-key">Email</span>
                    <span className="credits-profile-val" data-testid="credits-user-email">
                      {userProfile.email || '—'}
                    </span>
                  </div>
                  <div className="credits-profile-row">
                    <span className="credits-profile-key">Nombre</span>
                    <span className="credits-profile-val" data-testid="credits-user-name">
                      {userProfile.name || '—'}
                    </span>
                  </div>
                  <div className="credits-profile-row">
                    <span className="credits-profile-key">ID</span>
                    <span className="credits-profile-val credits-profile-id" data-testid="credits-user-id">
                      {userProfile.user_id}
                    </span>
                  </div>
                  <div className="credits-profile-row">
                    <span className="credits-profile-key">Rol</span>
                    <span
                      className="credits-profile-val credits-profile-role-badge"
                      style={{ color: roleConfig.color }}
                      data-testid="credits-user-role-value"
                    >
                      <RoleIcon size={14} />
                      {roleConfig.label}
                    </span>
                  </div>
                  <div className="credits-profile-row">
                    <span className="credits-profile-key">Créditos</span>
                    <span className="credits-profile-val" data-testid="credits-user-credits">
                      <span>{balance !== null ? balance : '--'}</span>
                    </span>
                  </div>
                  <div className="credits-profile-row">
                    <span className="credits-profile-key">Gasto Total</span>
                    <span className="credits-profile-val credits-total-spent" data-testid="credits-total-spent">
                      {totalSpent} créditos
                    </span>
                  </div>
                </div>
              )}

              {/* Capabilities */}
              <div className="credits-profile-capabilities">
                <div className="credits-profile-cap-title">Capacidades</div>
                <div className="credits-profile-cap-list">
                  {role === 'admin' && (
                    <>
                      <div className="credits-profile-cap ok">Otorgar créditos a otros usuarios</div>
                      <div className="credits-profile-cap ok">Ajustar saldos</div>
                      <div className="credits-profile-cap ok">Acceso completo a la API</div>
                      <div className="credits-profile-cap ok">Créditos deducidos y trazados como cualquier usuario</div>
                    </>
                  )}
                  {role === 'tester' && (
                    <>
                      <div className="credits-profile-cap ok">Probar todas las operaciones</div>
                      <div className="credits-profile-cap ok">Créditos deducidos y trazados como cualquier usuario</div>
                      <div className="credits-profile-cap no">No puede otorgar créditos</div>
                    </>
                  )}
                  {role === 'user' && (
                    <>
                      <div className="credits-profile-cap ok">Generación con créditos</div>
                      <div className="credits-profile-cap ok">Ver historial de uso</div>
                      <div className="credits-profile-cap no">Sin acceso admin</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* COSTS TAB */}
          {activeTab === 'costs' && (
            <div className="credits-costs-list" data-testid="credits-costs-list">
              <div className="credits-costs-header">
                <span>Operación</span>
                <span>Costo</span>
              </div>
              {costs.map((cost) => (
                <div key={cost.result_type} className="credits-cost-row" data-testid={`cost-row-${cost.result_type}`}>
                  <span className="credits-cost-label">
                    {cost.label || RESULT_TYPE_LABELS[cost.result_type] || cost.result_type}
                  </span>
                  <span className="credits-cost-value">
                    <Coins size={12} />
                    {cost.credits}
                  </span>
                </div>
              ))}
              {costs.length === 0 && (
                <div className="credits-empty">No hay datos de costos</div>
              )}
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === 'history' && (
            <div className="credits-history-list" data-testid="credits-history-list">
              {history.map((entry) => {
                const isDebit = entry.credits_delta < 0;
                const isZeroCost = entry.credits_delta === 0;
                return (
                  <div key={entry.id} className={`credits-history-row ${isZeroCost ? 'free' : isDebit ? 'debit' : 'credit'}`}>
                    <div className="credits-history-icon">
                      {isZeroCost ? (
                        <InfinityIcon size={16} />
                      ) : isDebit ? (
                        <ArrowDownCircle size={16} />
                      ) : (
                        <ArrowUpCircle size={16} />
                      )}
                    </div>
                    <div className="credits-history-info">
                      <span className="credits-history-reason">
                        {entry.reason
                          ? entry.reason.replace('execution:', '').split(':').map(
                              p => RESULT_TYPE_LABELS[p] || p
                            ).join(': ')
                          : 'Transacción'}
                      </span>
                      <span className="credits-history-date">{formatDate(entry.created_at)}</span>
                    </div>
                    <div className="credits-history-amount">
                      <span className={isZeroCost ? 'free-use' : isDebit ? 'negative' : 'positive'}>
                        {isZeroCost ? 'Gratis' : `${isDebit ? '' : '+'}${entry.credits_delta}`}
                      </span>
                      <span className="credits-history-balance">
                        Saldo: {entry.balance_after}
                      </span>
                    </div>
                  </div>
                );
              })}
              {history.length === 0 && (
                <div className="credits-empty">Sin transacciones recientes</div>
              )}
            </div>
          )}
        </div>
      </div>
      <BuyCreditsModal isOpen={showBuyModal} onClose={() => setShowBuyModal(false)} />
    </div>
  );
});

CreditsPanel.displayName = 'CreditsPanel';
export default CreditsPanel;
